import { notification } from "antd";
import { all, call, put, takeLatest } from "redux-saga/effects";
import {
  GENERATE_PASS_VIEW_REPORT,
  GENERATE_TLOG_REPORT,
  GENERATE_ZONE_VIEW_REPORT,
  REPORT_LOADER_START,
  REPORT_LOADER_STOP,
} from "./types.js";
import { ADMIN_URL } from "../../config.js";
import fetch from "../../services/fetch.js";

// Email validation helper function
const isValidEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

// Validate comma-separated emails
const validateEmails = (emailString) => {
  if (!emailString || typeof emailString !== "string") {
    return false;
  }

  // Split by comma and trim whitespace
  const emails = emailString.split(",").map((email) => email.trim());

  // Filter out empty strings and check if we have at least one email
  const validEmails = emails.filter((email) => email.length > 0);

  if (validEmails.length === 0) {
    return false;
  }

  // Check if all non-empty emails are valid
  return validEmails.every((email) => isValidEmail(email));
};

// Parameter validation helper function
const validateReportParams = (params) => {
  const { projectId, emailAddress } = params;

  if (!projectId || typeof projectId !== "number") {
    throw new Error("projectId is required and must be a number");
  }

  if (!emailAddress || !validateEmails(emailAddress)) {
    throw new Error(
      "emailAddress is required and must contain valid email addresses (comma-separated if multiple)"
    );
  }

  return true;
};

// Build query parameters helper function
const buildQueryParams = (params) => {
  const { projectId, emailAddress, weldNumbers, startDate, endDate } = params;
  const queryParams = new URLSearchParams();

  queryParams.append("projectId", projectId);
  queryParams.append("emailAddress", emailAddress);

  if (weldNumbers) {
    queryParams.append("weldNumbers", weldNumbers);
  }

  if (startDate) {
    queryParams.append("startDate", startDate);
  }

  if (endDate) {
    queryParams.append("endDate", endDate);
  }

  return queryParams.toString();
};

// Generate Pass View Report API call
function* generatePassViewReportSaga(action) {
  try {
    yield put({ type: REPORT_LOADER_START });

    // Validate parameters
    validateReportParams(action.payload);

    // Build query parameters
    const queryString = buildQueryParams(action.payload);

    console.log("Generating Pass View Report with params:", action.payload);

    const res = yield fetch(
      `${ADMIN_URL}/v1/reports/download-proxy/pass-view?${queryString}`,
      {
        method: "GET",
        headers: {
          Accept: "application/json",
        },
      }
    );

    if (res.status === 403) {
      throw new Error("Unauthorized access. Please check your permissions.");
    }

    const response = yield res.json();

    // Check for success response
    if (
      (res.status === 200 || res.status === 202) &&
      response.data.status === "Success"
    ) {
      notification.success({
        message:
          "Pass View report generation has been initiated. Please check your email for the report.",
        duration: 5,
      });
      return;
    }

    // Handle error cases
    const errorMessage =
      response?.data?.message || "Failed to generate Pass View report";
    notification.error({
      message: errorMessage,
      duration: 6,
    });
  } catch (error) {
    console.error("Pass View Report generation error:", error);
    const errorMessage =
      error.message || "Failed to generate Pass View report. Please try again.";
    notification.error({
      message: errorMessage,
      duration: 6,
    });
  } finally {
    yield put({ type: REPORT_LOADER_STOP });
  }
}

// Generate Zone View Report API call
function* generateZoneViewReportSaga(action) {
  try {
    yield put({ type: REPORT_LOADER_START });

    // Validate parameters
    validateReportParams(action.payload);

    // Build query parameters
    const queryString = buildQueryParams(action.payload);

    console.log("Generating Zone View Report with params:", action.payload);

    const res = yield fetch(
      `${ADMIN_URL}/v1/reports/download-proxy/zone-view?${queryString}`,
      {
        method: "GET",
        headers: {
          Accept: "application/json",
        },
      }
    );

    if (res.status === 403) {
      throw new Error("Unauthorized access. Please check your permissions.");
    }

    const response = yield res.json();

    // Check for success response
    if (
      (res.status === 200 || res.status === 202) &&
      response.data.status === "Success"
    ) {
      notification.success({
        message:
          "Zone View report generation has been initiated. Please check your email for the report.",
        duration: 5,
      });
      return;
    }

    // Handle error cases
    const errorMessage =
      response?.data?.message || "Failed to generate Zone View report";
    notification.error({
      message: errorMessage,
      duration: 6,
    });
  } catch (error) {
    console.error("Zone View Report generation error:", error);
    const errorMessage =
      error.message || "Failed to generate Zone View report. Please try again.";
    notification.error({
      message: errorMessage,
      duration: 6,
    });
  } finally {
    yield put({ type: REPORT_LOADER_STOP });
  }
}

function* generateTLogReportSaga(action) {
  try {
    yield put({ type: REPORT_LOADER_START });

    // Validate parameters
    validateReportParams(action.payload);

    // Build query parameters
    const queryString = buildQueryParams(action.payload);

    console.log("Generating T-logs Report with params:", action.payload);

    const res = yield fetch(`${ADMIN_URL}/v1/reports/t-logs?${queryString}`, {
      method: "GET",
      headers: {
        Accept: "application/json",
      },
    });

    if (res.status === 403) {
      throw new Error("Unauthorized access. Please check your permissions.");
    }

    const response = yield res.json();

    // Check for success response
    if (
      (res.status === 200 || res.status === 202) &&
      response.data.status === "Success"
    ) {
      notification.success({
        message:
          "T-logs report generation has been initiated. Please check your email for the report.",
        duration: 5,
      });
      return;
    }

    // Handle error cases
    const errorMessage =
      response?.data?.message || "Failed to generate T-logs report";
    notification.error({
      message: errorMessage,
      duration: 6,
    });
  } catch (error) {
    console.error("T-logs Report generation error:", error);
    const errorMessage =
      error.message || "Failed to generate T-logs report. Please try again.";
    notification.error({
      message: errorMessage,
      duration: 6,
    });
  } finally {
    yield put({ type: REPORT_LOADER_STOP });
  }
}

function* reportWatcher() {
  yield takeLatest(GENERATE_PASS_VIEW_REPORT, generatePassViewReportSaga);
  yield takeLatest(GENERATE_ZONE_VIEW_REPORT, generateZoneViewReportSaga);
  yield takeLatest(GENERATE_TLOG_REPORT, generateTLogReportSaga);
}

export default function* reportSagas() {
  yield all([call(reportWatcher)]);
}
